import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'trigger',
    'panel',
    'input',
    'selectedLabel',
    'searchInput',
    'item',
    'validationInput',
  ];

  static classes = ['selected'];

  #highlightedIndex = -1;
  #returnFocusOnClose = false;

  handleToggle(event) {
    if (event.newState === 'open') {
      this.triggerTarget.setAttribute('aria-expanded', 'true');
      this.searchInputTarget.value = '';
      this.#filterItems('');
      this.#highlightSelectedOrFirst(this.#visibleItems());
      this.searchInputTarget.focus();
    } else {
      this.triggerTarget.setAttribute('aria-expanded', 'false');
      if (this.#returnFocusOnClose) {
        this.#returnFocusOnClose = false;
        this.triggerTarget.focus();
      }
    }
  }

  filter(event) {
    this.#filterItems(event.target.value.toLowerCase());
    this.#highlightSelectedOrFirst(this.#visibleItems());
  }

  navigate(event) {
    const visible = this.#visibleItems();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#highlightedIndex = Math.min(
        this.#highlightedIndex + 1,
        visible.length - 1,
      );
      this.#applyHighlight(visible);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#highlightedIndex = Math.max(this.#highlightedIndex - 1, 0);
      this.#applyHighlight(visible);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const highlighted = visible[this.#highlightedIndex];
      if (highlighted) this.#selectItem(highlighted);
    } else if (event.key === 'Escape') {
      // The browser closes the popover natively on Escape.
      // Flag so handleToggle returns focus to the trigger on close.
      this.#returnFocusOnClose = true;
    }
  }

  select(event) {
    this.#selectItem(event.currentTarget);
  }

  #highlightSelectedOrFirst(visible) {
    this.#highlightedIndex = visible.findIndex(
      (item) => item.dataset.value === this.inputTarget.value,
    );
    if (this.#highlightedIndex === -1) this.#highlightedIndex = 0;
    this.#applyHighlight(visible);
  }

  #filterItems(term) {
    this.itemTargets.forEach((item) => {
      item.hidden = !item.dataset.label.toLowerCase().includes(term);
    });
  }

  #visibleItems() {
    return this.itemTargets.filter((item) => !item.hidden);
  }

  #applyHighlight(visible) {
    this.#clearHighlights();
    const highlighted = visible[this.#highlightedIndex];
    if (highlighted) {
      highlighted.firstElementChild.classList.add(this.selectedClass);
      highlighted.scrollIntoView({ block: 'nearest' });
      this.searchInputTarget.setAttribute(
        'aria-activedescendant',
        highlighted.id,
      );
    }
  }

  #clearHighlights() {
    this.itemTargets.forEach((item) =>
      item.firstElementChild.classList.remove(this.selectedClass),
    );
    this.searchInputTarget.removeAttribute('aria-activedescendant');
  }

  #selectItem(item) {
    this.itemTargets.forEach((i) => i.setAttribute('aria-selected', 'false'));
    item.setAttribute('aria-selected', 'true');
    this.inputTarget.value = item.dataset.value;
    this.selectedLabelTarget.textContent = item.dataset.label;
    if (this.hasValidationInputTarget) {
      this.validationInputTarget.value = item.dataset.value;
    }
    this.#returnFocusOnClose = true;
    this.panelTarget.hidePopover();
  }
}
